/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { connect, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { bindActionCreators, compose, Dispatch } from "redux";

import invert from "lodash/invert";
import { ActionButton, FontIcon, SearchBox, Stack } from "office-ui-fabric-react/lib";

import { PrimaryButton } from "@fluentui/react";

import * as AccessControlConstants from "Constants/AccessControlConstants";
import * as AccessControlActions from "Dataflows/AccessControl/AccessControlAction";
import * as TechnologyAreaActions from "Dataflows/TechnologyArea/TechnologyAreaAction";

import AccessControlList from "Components/AccessControlList/AccessControlList";
import ListCounts from "Components/Common/ListCounts/ListCounts";
import { Title } from "Components/Common/Results/Result.styles";
import { iconButtonStyles } from "Components/Common/SharedListStyles.styles";
import { RoleGuideList } from "Components/RoleGuideList/RoleGuideList";
import { OperationStatus } from "Constants/OperationStatus";
import { RoleNamesEnum } from "Constants/RolesPermissionsConstants";
import { SubSiteUrlRootPath, TenantTypesEnum } from "Constants/TenantTypes";
import {
  getAccessControlFilterValues,
  getCurrentPage,
  getCurrentTenant,
  getFilteredCount,
  getNetworkOperationStatus,
  getPageSize,
  getSearchText,
  getUsers,
} from "Dataflows/AccessControl/AccessControlSelector";
import { AppState } from "Dataflows/Reducers";
import { IFilterField } from "Interfaces/IFilterField";
import { IFilterValue } from "Interfaces/IFilterValue";
import { getTenant } from "Utility/Utils";

import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "../Common/CommonContainer.styles";
import SideBarContainer from "../SideBarContainer/SideBarContainer";
import { IAccessControlContainerProps } from "./IAccessControlContainerProps";

const fakeRoleGuideMspItems = {
  roles: [
    {
      id: "programOwner",
      name: RoleNamesEnum.PROGRAM_OWNER,
    },
    {
      id: "reviewer",
      name: RoleNamesEnum.COMMUNITY_MANAGER,
    },
    {
      id: "guest",
      name: RoleNamesEnum.GUEST,
    },
    {
      id: "benefitsManager",
      name: RoleNamesEnum.BENEFITS_MANAGER,
    },
  ],
  permissions: [
    {
      id: "viewUsers",
      name: "View Users",
      roles: ["programOwner", "benefitsManager", "reviewer"],
    },
    {
      id: "updateUsers",
      name: "Update Users",
      roles: ["programOwner", "reviewer"],
    },
    {
      id: "viewEvents",
      name: "View Events",
      roles: ["programOwner", "reviewer"],
    },
    {
      id: "reviewEvents",
      name: "Review Events",
      roles: ["programOwner", "reviewer"],
    },
    {
      id: "viewApplications",
      name: "View Applications",
      roles: ["programOwner", "reviewer", "guest"],
    },
    {
      id: "scoreReviewApplications",
      name: "Score/Review Applications",
      roles: ["programOwner", "reviewer", "guest"],
    },
    {
      id: "assignUpdateOwner",
      name: "Assign/Update Owner",
      roles: [],
    },
    {
      id: "assignUpdateMVPReviewers",
      name: "Assign/Update MVP Reviewers",
      roles: ["programOwner"],
    },
    {
      id: "assignUpdateGuest",
      name: "Assign/Update Guest",
      roles: ["programOwner", "reviewer"],
    },
    {
      id: "ManuallyAssignApplications",
      name: "Manually Assign Applications",
      roles: ["programOwner"],
    },
    {
      id: "viewBenefits",
      name: "View Benefits",
      roles: ["programOwner", "benefitsManager", "reviewer"],
    },
    {
      id: "updateBenefits",
      name: "Update Benefits",
      roles: ["programOwner", "benefitsManager"],
    },
  ],
};

const fakeRoleGuideRdItems = {
  roles: [
    {
      id: "programOwner",
      name: RoleNamesEnum.PROGRAM_OWNER,
    },
    {
      id: "benefitsManager",
      name: RoleNamesEnum.BENEFITS_MANAGER,
    },
    {
      id: "reviewer",
      name: RoleNamesEnum.COMMUNITY_MANAGER,
    },
    {
      id: "guest",
      name: RoleNamesEnum.GUEST,
    },
  ],
  permissions: [
    {
      id: "viewUsers",
      name: "View Users",
      roles: ["programOwner", "benefitsManager", "reviewer"],
    },
    {
      id: "updateUsers",
      name: "Update Users",
      roles: ["programOwner", "reviewer"],
    },
    {
      id: "viewEvents",
      name: "View Events",
      roles: ["programOwner", "reviewer"],
    },
    {
      id: "reviewEvents",
      name: "Review Events",
      roles: ["programOwner", "reviewer"],
    },
    {
      id: "viewApplications",
      name: "View Applications",
      roles: ["programOwner", "reviewer", "guest"],
    },
    {
      id: "scoreReviewApplications",
      name: "Score/Review Applications",
      roles: ["programOwner", "reviewer", "guest"],
    },
    {
      id: "assignUpdateOwner",
      name: "Assign/Update Owner",
      roles: [],
    },
    {
      id: "assignUpdateMVPReviewers",
      name: "Assign/Update MVP Reviewers",
      roles: ["programOwner"],
    },
    {
      id: "assignUpdateGuest",
      name: "Assign/Update Guest",
      roles: ["programOwner", "reviewer"],
    },
    {
      id: "ManuallyAssignApplications",
      name: "Manually Assign Applications",
      roles: ["programOwner"],
    },
    {
      id: "viewBenefits",
      name: "View Benefits",
      roles: ["programOwner", "benefitsManager", "reviewer"],
    },
    {
      id: "updateBenefits",
      name: "Update Benefits",
      roles: ["programOwner", "benefitsManager"],
    },
    {
      id: "viewDistributionLists",
      name: "View Distribution Lists",
      roles: ["programOwner", "benefitsManager", "reviewer"],
    },
    {
      id: "updateDistributionLists",
      name: "Update Distribution Lists",
      roles: ["programOwner", "benefitsManager"],
    },
    {
      id: "updateLoginEmail",
      name: "Update Login Email",
      roles: ["programOwner", "benefitsManager"],
    },
  ],
};

const fakeRoleGuideMvpItems = {
  roles: [
    {
      id: "businessGroup",
      name: RoleNamesEnum.BUSINESS_GROUP,
    },
    {
      id: "executive",
      name: RoleNamesEnum.EXECUTIVE,
    },
    {
      id: "productGroupPartner",
      name: RoleNamesEnum.PRODUCT_GROUP_PARTNER,
    },
    {
      id: "communityManager",
      name: RoleNamesEnum.COMMUNITY_MANAGER,
    },
    {
      id: "benefitsManager",
      name: RoleNamesEnum.BENEFITS_MANAGER,
    },
    {
      id: "programOwner",
      name: RoleNamesEnum.PROGRAM_OWNER,
    },
  ],
  permissions: [
    {
      id: "viewUsers",
      name: "View Users",
      roles: [
        "programOwner",
        "benefitsManager",
        "communityManager",
        "productGroupPartner",
        "businessGroup",
      ],
    },
    {
      id: "updateUsers",
      name: "Update Users",
      roles: ["programOwner", "communityManager", "businessGroup"],
    },
    {
      id: "viewAccessControl",
      name: "View Access Control",
      roles: ["programOwner", "communityManager", "businessGroup"],
    },
    {
      id: "editAccessControl",
      name: "Edit Access Control",
      roles: ["programOwner"],
    },
    {
      id: "viewDistributionLists",
      name: "View Distribution Lists",
      roles: [
        "programOwner",
        "benefitsManager",
        "communityManager",
        "productGroupPartner",
        "businessGroup",
      ],
    },
    {
      id: "updateDistributionLists",
      name: "Edit Distribution Lists",
      roles: [
        "programOwner",
        "benefitsManager",
        "communityManager",
        "productGroupPartner",
        "businessGroup",
      ],
    },
    {
      id: "viewBenefits",
      name: "View Benefit codes",
      roles: [
        "programOwner",
        "benefitsManager",
        "communityManager",
        "productGroupPartner",
        "businessGroup",
      ],
    },
    {
      id: "updateBenefits",
      name: "Claim benefits on behalf of user",
      roles: ["programOwner", "benefitsManager"],
    },
    {
      id: "viewApplications",
      name: "View Applications",
      roles: [
        "programOwner",
        "executive",
        "communityManager",
        "productGroupPartner",
        "businessGroup",
        "benefitsManager",
      ],
    },
    {
      id: "viewDemographics",
      name: "View Demographics",
      roles: ["programOwner", "communityManager", "businessGroup", "benefitsManager"],
    },
    {
      id: "assignScoreApplications",
      name: "Assign/Score Applications",
      roles: ["programOwner", "productGroupPartner"],
    },
    {
      id: "adjustApplications",
      name: "Adjust application status and resolve flags",
      roles: ["programOwner", "businessGroup"],
    },
    {
      id: "flagApplications",
      name: "Flag applications",
      roles: ["executive", "communityManager"],
    },
    {
      id: "updateLoginEmail",
      name: "Update Login Email",
      roles: ["programOwner", "benefitsManager"],
    },
    {
      id: "grantAnnualException",
      name: "Grant Annual Exception",
      roles: ["programOwner", "businessGroup"],
    },
    {
      id: "updateApplicationTechnologyArea",
      name: "Update the TA and Reset the score",
      roles: ["programOwner", "businessGroup"],
    },
  ],
};

/**
 *
 * @param tenant Tenant string param.
 * @returns Fake (for now) role guide items.
 * TODO: The return will need to be typed once we have the real data.
 */
const roleGuideItems = (tenant: string) => {
  if (tenant === TenantTypesEnum.MLSA) {
    return fakeRoleGuideMspItems;
  } else if (tenant === TenantTypesEnum.RD) {
    return fakeRoleGuideRdItems;
  } else {
    return fakeRoleGuideMvpItems;
  }
};

/**
 *
 * @param tenant Tenant string param.
 * @returns Access control filter fields.
 */
const getAccessControlFilterFields = (tenant: string): IFilterField[] => {
  const tenantType = getTenant(tenant as TenantTypesEnum);

  if (tenantType === TenantTypesEnum.MLSA) {
    return AccessControlConstants.mlsaAccessControlFilterFields;
  } else if (tenantType === TenantTypesEnum.RD) {
    return AccessControlConstants.rdAccessControlFilterFields;
  } else {
    return AccessControlConstants.mvpAccessControlFilterFields;
  }
};

/**
 * Access Control Container element that displays side bar filtering and result listing.
 * @FunctionComponent
 * @param {IAccessControlContainerProps} props The side bar props.
 * @returns {React.FunctionComponent<IAccessControlContainerProps>} The side bar component.
 */
const AccessControlContainer: React.FunctionComponent<IAccessControlContainerProps> = (
  props: IAccessControlContainerProps
): ReactElement => {
  const dispatch = useDispatch();
  const {
    actions: {
      addAccessControlFilterValue,
      removeAccessControlFilterValue,
      removeAccessControlProvinceFilterValue,
      removeAccessControlCountryFilterValue,
      searchUsers,
      updateAccessControlFilterValue,
      resetPagination,
    },
    filteredCount,
    history,
    currentPage,
    pageSize,
    selectedUsersFilterValues,
    users,
    tenant,
    activeTenant,
    networkStatus,
    match: { params },
  } = props;

  const [showRoleGuide, setShowRoleGuide] = useState(false);
  const [searchText, setSearchText] = useState(props.searchText);

  if (!tenant) {
    history.push("/notFound");
  }

  useEffect(() => {
    if (tenant === TenantTypesEnum.MVP) {
      dispatch(TechnologyAreaActions.getTechnologyAreas(tenant));
    }
  }, []);

  // TODO: Get this from Saga API Call once backend is ready.
  const [roleAndPermissions, setRoleAndPermissions] = useState(roleGuideItems(tenant));

  const hasPermission = (roleId: string, permissionId: string): boolean => {
    const permission = roleAndPermissions.permissions.find((p) => p.id === permissionId);
    if (permission) {
      return permission.roles.includes(roleId);
    } else {
      return false;
    }
  };

  const items = roleAndPermissions.permissions.map((permission) => {
    const row = {
      permission: permission.name,
    };
    roleAndPermissions.roles.map((role) => {
      row[role.id] = (
        <FontIcon iconName={hasPermission(role.id, permission.id) ? "Accept" : "Cancel"} />
      );
    });
    return row;
  });

  /**
   * Call search function for entered text.
   * @method
   * @returns {void}
   */
  const handleSearch = (
    searchValue: string,
    selectedUsersFilterValues: IFilterValue[],
    page: number,
    pageSize: number
  ): void => {
    searchUsers(
      searchValue,
      getTenant(TenantTypesEnum[tenant]),
      selectedUsersFilterValues,
      page,
      pageSize
    );
  };

  /**
   * Call to reset filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    resetPagination();

    setSearchText("");
    handleSearch(
      "",
      AccessControlConstants.defaultFilterValues,
      1,
      AccessControlConstants.defaultListingPageSize
    );
  };

  useEffect(() => {
    if (networkStatus === OperationStatus.Pending) return;

    if (tenant !== activeTenant) {
      resetFilterValues();
      return;
    }

    handleSearch(searchText, selectedUsersFilterValues, currentPage, pageSize);
  }, [currentPage, pageSize]);

  /**
   * Function triggered when search box is cleared
   * @param ev clear event that was trigger
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onClear = (ev: any): void => {
    handleSearch("", selectedUsersFilterValues, 1, pageSize);
  };

  return (
    <Stack horizontal styles={containerStackStyles}>
      {!showRoleGuide && (
        <Stack.Item disableShrink>
          <SideBarContainer
            addFilterValue={addAccessControlFilterValue}
            removeFilterValue={removeAccessControlFilterValue}
            removeFilterProvinceValue={removeAccessControlProvinceFilterValue}
            removeFilterCountryValue={removeAccessControlCountryFilterValue}
            updateFilterValue={updateAccessControlFilterValue}
            selectedFilterValues={selectedUsersFilterValues}
            filterFields={getAccessControlFilterFields(TenantTypesEnum[tenant])}
          />
          <Stack horizontal tokens={buttonStackTokens}>
            <PrimaryButton
              styles={filterButtonStyles()}
              text="Apply"
              onClick={(): void => handleSearch(searchText, selectedUsersFilterValues, 1, pageSize)}
            />
            <PrimaryButton styles={filterButtonStyles()} text="Reset" onClick={resetFilterValues} />
          </Stack>
        </Stack.Item>
      )}
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <PaddedFlexRow>
            <Title>
              {showRoleGuide ? "Access Control - Role and Permissions table" : "Access Control"}
            </Title>
            {showRoleGuide ? (
              <ActionButton
                iconProps={{ iconName: "RevToggleKey" }}
                ariaLabel="Back to Access Control"
                onClick={(): void => setShowRoleGuide(false)}
              >
                Back to Access Control
              </ActionButton>
            ) : (
              <ActionButton
                iconProps={{ iconName: "TextDocumentShared" }}
                ariaLabel="Back to Access Control"
                styles={iconButtonStyles}
                onClick={(): void => setShowRoleGuide(true)}
              >
                Role Guide
              </ActionButton>
            )}
            <ActionButton
              iconProps={{ iconName: "Add" }}
              ariaLabel="Add User"
              styles={iconButtonStyles}
              onClick={(): void =>
                history.push(`/${invert(SubSiteUrlRootPath)[tenant]}/access/addUser`)
              }
            >
              Add User
            </ActionButton>
            <ListCounts total={filteredCount} loading={false} />
          </PaddedFlexRow>
          {!showRoleGuide && (
            <SearchBox
              placeholder="Search users"
              value={searchText}
              onSearch={(searchValue: string): void =>
                handleSearch(searchValue, selectedUsersFilterValues, 1, pageSize)
              }
              onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
                setSearchText(newValue || "")
              }
              onClear={onClear}
              styles={searchBoxStyles}
            />
          )}

          {showRoleGuide ? (
            <RoleGuideList items={items} roles={roleAndPermissions.roles} />
          ) : (
            /** @ts-ignore*/
            <AccessControlList
              data={users}
              pageSize={pageSize}
              tenant={tenant}
              totalCount={filteredCount}
              currentPage={currentPage}
            />
          )}
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

/**
 * Maps AccessControlContainer to redux state
 * @param {AppState} state The App state.
 * @param {IAccessControlContainerProps} ownProps the component props.
 */
const mapStateToProps = (state: AppState) => ({
  currentPage: getCurrentPage(state.accessControlState),
  networkStatus: getNetworkOperationStatus(state.accessControlState),
  pageSize: getPageSize(state.accessControlState),
  searchText: getSearchText(state.accessControlState),
  selectedUsersFilterValues: getAccessControlFilterValues(state.accessControlState),
  filteredCount: getFilteredCount(state.accessControlState),
  users: getUsers(state.accessControlState),
  activeTenant: getCurrentTenant(state.accessControlState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<AccessControlActions.AccessControlActionTypes>) => ({
  actions: {
    searchUsers: bindActionCreators(AccessControlActions.searchUsers, dispatch),
    resetPagination: bindActionCreators(AccessControlActions.resetPaginationProps, dispatch),
    addAccessControlFilterValue: bindActionCreators(
      AccessControlActions.addAccessControlFilterValue,
      dispatch
    ),
    removeAccessControlFilterValue: bindActionCreators(
      AccessControlActions.removeAccessControlFilterValue,
      dispatch
    ),
    removeAccessControlProvinceFilterValue: bindActionCreators(
      AccessControlActions.removeAccessControlFilterProvinceValue,
      dispatch
    ),
    removeAccessControlCountryFilterValue: bindActionCreators(
      AccessControlActions.removeAccessControlFilterCountryValue,
      dispatch
    ),
    updateAccessControlFilterValue: bindActionCreators(
      AccessControlActions.updateAccessControlFilterValue,
      dispatch
    ),
  },
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withRouter, withConnect)(AccessControlContainer) as React.FC<any>;
